import React, { useState, useEffect } from 'react'
import { useSelector } from 'react-redux'
import classNames from 'classnames'
import { nanoid } from 'nanoid'

import styles from '../component-styles/campaign-codes-administration.module.scss'
import Input, { Label } from './input'
import {
  CampaignCodeGeneratorStructure,
  GeneratorFields,
} from '../api-client/api-types'
import Modal, { ModalHead, ModalBody, ModalFooter } from './modal'
import Heading from './heading'
import ConfirmationButtons from './confirmation-buttons'
import ToolTip from './tooltip'
import { LabelSlot } from './row'
import { checkParamNameIsInvalid } from '../helpers/campaign-codes'
import { SelectBoxSimple } from './select-box'

const paramDefTemplate: GeneratorFields = {
  fieldID: 'NEW_ID',
  fieldAvailable: true,
  fieldName: '',
  fieldType: 'select',
  helpText: '',
  prefix: '',
  required: true,
  metaParameter: false,
  selectFields: [],
}

interface Props {
  onSubmit: (newParamDef: GeneratorFields) => void
  active: boolean
  toggleActive: () => void
}

export default function CampaignCodeAdministrationAddNew({
  onSubmit,
  active,
  toggleActive,
}: Props): React.ReactElement {
  const { generatedStructure } = useSelector(
    (
      s: any,
    ): {
      generatedStructure: CampaignCodeGeneratorStructure
    } => s.campaignCodes,
  )
  const dateOptions = [
    {
      name: 'Day (UK, DD-MM-YYYY)',
      value: 'DD-MM-YYYY',
    },
    {
      name: 'Day (UK, DDMMYYYY)',
      value: 'DDMMYYYY',
    },
    {
      name: 'Day (US, MM-DD-YYYY)',
      value: 'MM-DD-YYYY',
    },
    {
      name: 'Day (US, MMDDYYYY)',
      value: 'MMDDYYYY',
    },
    {
      name: 'Quarter (YYYYq#)',
      value: 'YYYY[q]Q',
    },
    {
      name: 'Month (YYYYMM)',
      value: 'YYYYMM',
    },
    {
      name: 'Year (YYYY)',
      value: 'YYYY',
    },
  ]
  const [loading, setLoading] = useState(false)
  const [dateFormat, setDateFormat] = useState('')

  const [newParamDef, setNewParamDef] = useState<GeneratorFields>({
    ...paramDefTemplate,
  })

  useEffect(() => {
    setDateFormat(dateOptions[0].value)
  }, [])

  const [fieldNameError, setFieldNameError] = useState(false)

  if (!active) {
    return <></>
  }

  return (
    <Modal
      active={active}
      toggleActive={toggleActive}
      type="white"
      narrow
      loading={loading}
    >
      <ModalHead>
        <Heading type={3} align="left">
          Add parameter
        </Heading>
      </ModalHead>
      <ModalBody>
        <LabelSlot column>
          <Label modalHeading id="parameter-name">
            Parameter name
          </Label>
          <ToolTip
            showUnder
            message="The name of each parameter input, visible to your users in the Track>Create page. These should be easy for your other Uplifter users to understand."
          />
        </LabelSlot>
        <Input
          id="parameter-name"
          name="parameter-name"
          label="Parameter name"
          value={newParamDef.fieldName}
          error={fieldNameError}
          className={classNames({
            [styles.hasError]: fieldNameError,
          })}
          onChange={(e: React.ChangeEvent<HTMLInputElement>): void => {
            const { value: val } = e.target as HTMLInputElement

            if (
              generatedStructure.paramDefs &&
              Array.isArray(generatedStructure.paramDefs) &&
              !checkParamNameIsInvalid(
                val,
                generatedStructure.paramDefs.map(
                  (paramDef) => paramDef.fieldName,
                ),
                setFieldNameError,
              )
            ) {
              setNewParamDef({
                fieldID: 'NEW_ID',
                ...newParamDef,
                fieldName: val,
              })
            }
          }}
        />
        {fieldNameError && (
          <p className={styles.footNoteError}>
            A parameter with that name already exists.
          </p>
        )}
        <Input
          type="checkbox"
          id="required"
          name="required"
          label="Required"
          checked={newParamDef.required}
          onChange={(e: React.ChangeEvent<HTMLInputElement>): void => {
            const { checked } = e.target as HTMLInputElement
            setNewParamDef({
              ...newParamDef,
              required: checked,
            })
          }}
        >
          <ToolTip message="If checked, users will have to complete this field to create a campaign link." />
        </Input>
        <Input
          type="checkbox"
          id="meta"
          name="meta"
          label="Meta parameter"
          checked={newParamDef.metaParameter}
          onChange={(e: React.ChangeEvent<HTMLInputElement>): void => {
            const { checked } = e.target as HTMLInputElement
            setNewParamDef({
              ...newParamDef,
              metaParameter: checked,
            })
          }}
        >
          <ToolTip message="If checked, this parameter data will only be stored in Uplifter and will not be visible in your campaign link (or your analytics tools). Designed for campaign link notes." />
        </Input>

        <LabelSlot column>
          <Label modalHeading id="prefix">
            Prefix
          </Label>
          <ToolTip message="The query string parameter name which always appears before the typed or selected value. Example: utm_campaign= is the prefix for the Google Analytics campaign value. This may be a symbol when concatenating multiple inputs into one parameter." />
        </LabelSlot>
        <Input
          id="prefix"
          name="prefix"
          label="New parameter prefix"
          value={newParamDef.prefix}
          onChange={(e: React.ChangeEvent<HTMLInputElement>): void => {
            const { value: val } = e.target as HTMLInputElement
            setNewParamDef({
              ...newParamDef,
              prefix: val,
            })
          }}
        />
        <LabelSlot column>
          <Label modalHeading id="help-text">
            Help text
          </Label>
          <ToolTip message="Text designed to help users enter the right values on the Track>Create page. Text will show when the users hovers over a tooltip next to each parameter name. " />
        </LabelSlot>
        <Input
          id="help-text"
          name="help-text"
          label="New parameter help text"
          value={newParamDef.helpText}
          onChange={(e: React.ChangeEvent<HTMLInputElement>): void => {
            const { value: val } = e.target as HTMLInputElement
            setNewParamDef({
              ...newParamDef,
              helpText: val,
            })
          }}
        />
        <Label modalHeading id="type">
          Input type
        </Label>
        <Input
          className={styles.iconDropDown}
          name="select-type"
          label="Dropdown"
          type="radio"
          value={newParamDef.fieldType}
          checked={newParamDef.fieldType === 'select'}
          onClick={(): void =>
            setNewParamDef({
              ...newParamDef,
              fieldType: 'select',
            })
          }
        >
          <ToolTip message="Users select a value from a list of options. Dropdown options are maintained by your Uplifter admins in **Track > Edit** page." />
        </Input>
        <Input
          className={styles.iconInput}
          name="text-type"
          label="Free text"
          type="radio"
          value={newParamDef.fieldType}
          checked={newParamDef.fieldType === 'input'}
          onClick={(): void =>
            setNewParamDef({
              ...newParamDef,
              fieldType: 'input',
            })
          }
        >
          <ToolTip message="Users can type or paste values, with data validation rules selected by your Uplifter admins." />
        </Input>
        <div className={styles.inline}>
          <Input
            className={styles.iconUKDate}
            name="date-type"
            label="Date picker"
            type="radio"
            value={newParamDef.fieldType}
            checked={newParamDef.fieldType === 'date'}
            onClick={(): void =>
              setNewParamDef({
                ...newParamDef,
                fieldType: 'date',
                dateFormat: dateOptions[0].value,
              })
            }
          />
          <ToolTip
            className={styles.tooltip}
            message="Users can select a date, month, year or quarter from a calendar visualisation. You can choose the date format (how it will appear in the query string parameter). If you require another date format, contact [support@uplifter.ai](mailto:support@uplifter.ai)"
          />
          <SelectBoxSimple
            className={styles.selectBox}
            name="date"
            value={dateFormat}
            onChange={(val) => {
              setDateFormat(val)
              setNewParamDef({
                ...newParamDef,
                fieldType: 'date',
                dateFormat: val,
              })
            }}
          >
            {dateOptions.map((i) => (
              <option key={nanoid()} value={i.value}>
                {i.name}
              </option>
            ))}
          </SelectBoxSimple>
        </div>
      </ModalBody>
      <ModalFooter>
        <ConfirmationButtons
          type="simple"
          onYes={async () => {
            const data = {
              ...paramDefTemplate,
              ...newParamDef,
            }
            if (newParamDef.fieldType === 'select') {
              data.selectFields = [
                { hide: false, optionName: 'Blank', optionValue: '' },
              ]
            }

            setLoading(true)
            await onSubmit(data)
            setLoading(false)

            setNewParamDef({ ...paramDefTemplate })
            toggleActive()
          }}
          onNo={(): void => {
            toggleActive()
          }}
          yesLabel="Add parameter"
          noLabel="Go back"
          disabled={fieldNameError || newParamDef.fieldName === ''}
        />
      </ModalFooter>
    </Modal>
  )
}
